import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { TextField, Button, Alert, Box, Typography } from '@mui/material';
import AuthCard from '../../components/AuthCard';
import TurnstileWidget from '../../components/TurnstileWidget';
import { useAuth } from '../../auth/AuthContext';
import { TURNSTILE_SITE_KEY } from '../../config';
import { formatAuthError } from '../../auth/formatAuthError';

export default function RegisterPage() {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  // Anti-bot challenge token (ADR-0021). Only required when a site key is
  // configured; otherwise the widget renders nothing and the token stays ''.
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRequired = Boolean(TURNSTILE_SITE_KEY);
  const { register, handleSubmit, formState: { isSubmitting } } = useForm();

  const onSubmit = async ({ firstName, lastName, email, password }) => {
    setError('');
    if (captchaRequired && !captchaToken) {
      setError('Please complete the "I\'m human" check before continuing.');
      return;
    }
    try {
      await registerUser({ firstName, lastName, email, password, captchaToken });
      navigate('/confirm', { state: { email } });
    } catch (e) {
      // A consumed/expired token can't be reused — clear it so the widget reissues.
      setCaptchaToken('');
      setError(formatAuthError(e.message));
    }
  };

  return (
    <AuthCard title="Create your account" subtitle="Start building with Vectros">
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="First name" size="small" fullWidth {...register('firstName', { required: true })} />
          <TextField label="Last name" size="small" fullWidth {...register('lastName', { required: true })} />
        </Box>
        <TextField label="Email" type="email" size="small" fullWidth {...register('email', { required: true })} />
        <TextField label="Password" type="password" size="small" fullWidth
          {...register('password', { required: true, minLength: 8 })}
          helperText="Minimum 8 characters" />
        {captchaRequired && (
          <TurnstileWidget siteKey={TURNSTILE_SITE_KEY} onToken={setCaptchaToken} />
        )}
        <Button type="submit" variant="contained" fullWidth
          disabled={isSubmitting || (captchaRequired && !captchaToken)}
          sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
          {isSubmitting ? 'Creating account…' : 'Create account'}
        </Button>
      </Box>
      <Typography variant="body2" textAlign="center" mt={2} color="text.secondary">
        Already have an account?{' '}
        <Link to="/login" style={{ color: '#3B82F6', fontWeight: 500 }}>Sign in</Link>
      </Typography>
    </AuthCard>
  );
}
